import { memo, useMemo } from "react";

import type { DiscountOrdinaryNewItemForOrdinaryQuery } from "@/lib/gql/__generated__/graphql";
import { DiscountType } from "@/lib/gql/__generated__/graphql";

import { FeatureIslandContainer, FeatureIslandMain } from "@/components/features/_layouts";

import {
	AccordionLayout,
	DiscountCodeFormFieldComposition,
	DiscountDescriptionFormFieldComposition,
	DiscountEditHeaderComposition,
	DiscountNameFormFieldComposition,
	FeatureIslandLayout,
	IsUncancelableFormFieldComposition,
	Layout,
	OrdinaryFormFieldComposition,
	OrdinaryWithRatio,
	OrdinaryWithStatic,
	PriceTotalLimitAmountFormFieldComposition,
	ResourceLowestPriceFormFieldComposition,
	SaleTypeForPlanFormFieldComposition,
	SaleTypeForTermFormFieldComposition,
	StayableDayOfTheWeekFormFieldComposition,
} from "../../../_shared";
import { DiscountEligibleComposition } from "../../../_shared/compositions/form-field/discount-eligible";
import { useInitialized } from "../model";

type Props = {
	hotelID: string;
	discountOrdinaryNewItemForOrdinaryServerModel: DiscountOrdinaryNewItemForOrdinaryQuery;
};

export const DiscountOrdinaryNewPagePresenter = memo(
	({ hotelID, discountOrdinaryNewItemForOrdinaryServerModel }: Props) => {
		const {
			viewModel,
			// mutation
			onCreateDiscountOrdinary,

			//
			// 公開にする
			onPublish,
			// 非公開にする
			onUnpublish,

			// 基本設定
			// ディスカウント名の変更
			onChangeName,
			// ディスカウント説明の変更
			onChangeDescription,
			// ディスカウントコードの変更
			onChangeDiscountCode,
			// ディスカウント対象の変更
			onChangeIsPlanDiscountEligible,
			// 部屋のディスカウント対象の変更
			onChangeIsRoomDiscountEligible,
			// オプションのディスカウント対象の変更
			onChangeIsOptionDiscountEligible,
			// 追加料金のディスカウント対象の変更
			onChangeIsAdditionalFeeDiscountEligible,
			// ディスカウント方法の変更
			onChangeDiscountType,
			// ディスカウント金額の変更（一定割合）
			onChangeDiscountWithRatio,
			// ディスカウント金額の変更（固定金額）
			onChangeDiscountWithStatic,
			// ディスカウント方法の変数の変更（顧客属性）
			onChangeStaticTargetType,
			// ディスカウント方法の変数の変更（期間）
			onChangeStaticTermType,

			// 追加設定
			// 最低価格の変更
			onChangeResourceLowestPrice,
			// ディスカウント上限金額を設定しない
			onUnSettingDiscountPriceTotalLimit,
			// ディスカウント上限金額の変更
			onChangeDiscountPriceTotalLimit,
			// ディスカウントをプラン限定にするかの変更
			onChangeSaleTypeForPlan,
			// キャンセル不可のディスカウントにするかの変更
			onChangeIsUncancelable,
			// ディスカウントの利用可能期間を設定するかの変更
			onChangeSaleTypeForTerm,
			// 公開期間を変更
			onChangeSale,
			// 利用可能期間を変更
			onChangeStayable,
			// 利用可能曜日の変更
			onChangeStayableDayOfTheWeek,
		} = useInitialized({ hotelID, discountOrdinaryNewItemForOrdinaryServerModel });

		/**
		 * @todo
		 * ViewModel でエラーを保持しないようにしている
		 * 利用可能な曜日のエラーを ViewModel で管理していないため、ViewModel が保持しているエラーと利用可能な曜日のエラーを統合する
		 */
		const hasError = useMemo(() => {
			return viewModel.hasError || viewModel.stayableDayOfTheWeek?.length === 0;
		}, [viewModel.hasError, viewModel.stayableDayOfTheWeek?.length]);

		return (
			<FeatureIslandContainer>
				<DiscountEditHeaderComposition
					title="通常ディスカウントの設定"
					inSale={viewModel.inSale}
					isDirty={viewModel.isDirty}
					hasError={hasError}
					onPublish={onPublish}
					onUnpublish={onUnpublish}
					onSave={onCreateDiscountOrdinary}
				/>
				<FeatureIslandMain>
					<Layout className="mx-auto grid max-w-screen-lg gap-y-12">
						<FeatureIslandLayout title="基本設定">
							<Layout className="divide-y divide-zinc-100 [&>div]:p-3">
								{/* ディスカウントの名前の設定 */}
								<DiscountNameFormFieldComposition
									discountName={viewModel.name}
									//
									onChangeName={onChangeName}
								/>

								{/* ディスカウントの説明の設定 */}
								<DiscountDescriptionFormFieldComposition
									discountDescription={viewModel.description}
									//
									onChangeDescription={onChangeDescription}
								/>

								{/* ディスカウントコードの設定 */}
								<DiscountCodeFormFieldComposition
									discountCode={viewModel.discountCode}
									//
									onChangeCode={onChangeDiscountCode}
								/>

								{/* ディスカウント対象の設定 */}
								<DiscountEligibleComposition
									isPlanDiscountEligible={viewModel.isPlanDiscountEligible?.value ?? false}
									isRoomDiscountEligible={viewModel.isRoomDiscountEligible?.value ?? false}
									isOptionDiscountEligible={viewModel.isOptionDiscountEligible?.value ?? false}
									isAdditionalFeeDiscountEligible={
										viewModel.isAdditionalFeeDiscountEligible?.value ?? false
									}
									//
									onChangePlanDiscountEligible={onChangeIsPlanDiscountEligible}
									onChangeRoomDiscountEligible={onChangeIsRoomDiscountEligible}
									onChangeOptionDiscountEligible={onChangeIsOptionDiscountEligible}
									onChangeAdditionalFeeDiscountEligible={onChangeIsAdditionalFeeDiscountEligible}
								/>

								{/* 通常ディスカウントの設定 */}
								<OrdinaryFormFieldComposition
									discountType={viewModel.discountType}
									//
									onChangeDiscountType={onChangeDiscountType}
								>
									{viewModel.discountType === DiscountType.Ratio ? (
										<OrdinaryWithRatio
											ratio={viewModel.ratio}
											//
											onChangeDiscountWithRatio={onChangeDiscountWithRatio}
										/>
									) : (
										<OrdinaryWithStatic
											staticAmount={viewModel.static.amount}
											staticTargetType={viewModel.staticTargetType}
											staticTermType={viewModel.staticTermType}
											//
											onChangeDiscountWithStatic={onChangeDiscountWithStatic}
											onChangeStaticTargetType={onChangeStaticTargetType}
											onChangeStaticTermType={onChangeStaticTermType}
										/>
									)}
								</OrdinaryFormFieldComposition>
								{/* 提供最低価格の設定 */}
								<ResourceLowestPriceFormFieldComposition
									resourceLowestPriceAmount={viewModel.resourceLowestPrice.amount}
									onChangeResourceLowestPrice={onChangeResourceLowestPrice}
								/>
							</Layout>
						</FeatureIslandLayout>

						<FeatureIslandLayout title="追加設定">
							{/* 合計上限価格の設定 */}
							<AccordionLayout
								title="ディスカウント合計上限金額に関する設定"
								id="discount-price-total-limit-settings"
							>
								<PriceTotalLimitAmountFormFieldComposition
									discountPriceTotalLimitAmount={viewModel.discountPriceTotalLimit?.amount}
									onUnSettingDiscountPriceTotalLimit={onUnSettingDiscountPriceTotalLimit}
									onChangeDiscountPriceTotalLimit={onChangeDiscountPriceTotalLimit}
								/>
							</AccordionLayout>

							{/* プラン限定の設定 */}
							<AccordionLayout
								title="プラン限定のディスカウントに関する設定"
								id="discount-sale-type-for-plan-settings"
							>
								<SaleTypeForPlanFormFieldComposition
									saleTypeForPlan={viewModel.saleTypeForPlan}
									onChangeSaleTypeForPlan={onChangeSaleTypeForPlan}
								/>
							</AccordionLayout>

							{/* キャンセル不可のディスカウントにするかの設定 */}
							<AccordionLayout
								title="キャンセル不可のディスカウントに関する設定"
								id="discount-is-uncancelable"
							>
								<IsUncancelableFormFieldComposition
									isUncancelable={viewModel.isUncancelable}
									onChangeIsUncancelable={onChangeIsUncancelable}
								/>
							</AccordionLayout>

							{/* 利用可能な期間の設定 */}
							<AccordionLayout
								title="利用可能な期間に関する設定"
								id="discount-sale-type-for-term-settings"
							>
								<SaleTypeForTermFormFieldComposition
									saleTypeForTerm={viewModel.saleTypeForTerm}
									saleStart={viewModel.saleStart ?? undefined}
									saleEnd={viewModel.saleEnd ?? undefined}
									stayableStart={viewModel.stayableStart ?? undefined}
									stayableEnd={viewModel.stayableEnd ?? undefined}
									onChangeSaleTypeForTerm={onChangeSaleTypeForTerm}
									onChangeSale={onChangeSale}
									onChangeStayable={onChangeStayable}
								/>
							</AccordionLayout>

							{/* 利用可能な曜日の設定 */}
							<AccordionLayout
								title="利用可能な曜日に関する設定"
								id="discount-stayable-day-of-the-week-settings"
							>
								<StayableDayOfTheWeekFormFieldComposition
									stayableDayOfTheWeek={viewModel.stayableDayOfTheWeek ?? []}
									onChangeStayableDayOfTheWeek={onChangeStayableDayOfTheWeek}
								/>
							</AccordionLayout>
						</FeatureIslandLayout>
					</Layout>
				</FeatureIslandMain>
			</FeatureIslandContainer>
		);
	},
);
